import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { questionsService, votesService } from '../services/api';
import { Question } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';

type TabType = 'popular' | 'recent' | 'answered';

export default function HomePage() {
  const { user, getAccessToken } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('popular');
  const [searchQuery, setSearchQuery] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const fetchQuestions = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await questionsService.getAll(token);
      if (response.success && response.data) {
        setQuestions(response.data);
        setError(null);
      } else {
        setError(response.error || 'Failed to fetch questions');
      }
    } catch (err) {
      setError('Failed to fetch questions');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newQuestion.trim() || isSubmitting) return;

    const token = await getAccessToken();
    if (!token) return;

    setIsSubmitting(true);
    try {
      const response = await questionsService.create(token, newQuestion.trim(), isAnonymous);
      if (response.success) {
        setNewQuestion('');
        setIsAnonymous(false);
        setSubmitSuccess(true);
        setTimeout(() => setSubmitSuccess(false), 3000);
        fetchQuestions();
      } else {
        setError(response.error || 'Failed to submit question');
      }
    } catch (err) {
      setError('Failed to submit question');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVote = async (questionId: number) => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await votesService.addVote(token, questionId);
      if (response.success) {
        setQuestions((prev) =>
          prev.map((q) =>
            q.id === questionId
              ? { ...q, voteCount: q.voteCount + 1, hasUserVoted: true }
              : q
          )
        );
      }
    } catch (err) {
      console.error('Vote error:', err);
    }
  };

  const handleUnvote = async (questionId: number) => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await votesService.removeVote(token, questionId);
      if (response.success) {
        setQuestions((prev) =>
          prev.map((q) =>
            q.id === questionId
              ? { ...q, voteCount: q.voteCount - 1, hasUserVoted: false }
              : q
          )
        );
      }
    } catch (err) {
      console.error('Unvote error:', err);
    }
  };

  const filteredQuestions = questions.filter((q) => {
    const matchesSearch =
      !searchQuery ||
      q.questionText.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.createdByName.toLowerCase().includes(searchQuery.toLowerCase());

    if (activeTab === 'answered') {
      return matchesSearch && q.status === 'answered';
    }
    return matchesSearch;
  });

  const sortedQuestions = [...filteredQuestions].sort((a, b) => {
    if (activeTab === 'popular') {
      return b.voteCount - a.voteCount;
    }
    return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
  });

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with question form - Reflectiz blue */}
      <div className="text-white"   style={{
    background: 'linear-gradient(180deg, #4568FF 0%, #203D75 100%)',
  }}>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <h1 className="text-3xl font-bold mb-2">Ask a Question</h1>
          <p className="text-blue-200 mb-6">
            Submit your questions and vote on questions you'd like answered
          </p>

          <form onSubmit={handleSubmit}>
            <div className="space-y-3">
              <textarea
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder="Type your question here..."
                rows={3}
                className="w-full px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                maxLength={2000}
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-blue-300">
                    {newQuestion.length}/2000 characters
                  </span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-white">Submit anonymously</span>
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={!newQuestion.trim() || isSubmitting}
                  className="px-6 py-2.5 font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#74ff9b', color: '#103a77' }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Question'}
                </button>
              </div>
            </div>
          </form>

          {submitSuccess && (
            <div className="mt-4 p-3 bg-green-600/20 border border-green-500/30 rounded-lg text-green-100">
              Question submitted successfully!
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Search and tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <TabButton
              active={activeTab === 'popular'}
              onClick={() => setActiveTab('popular')}
            >
              Popular
            </TabButton>
            <TabButton
              active={activeTab === 'recent'}
              onClick={() => setActiveTab('recent')}
            >
              Recent
            </TabButton>
            <TabButton
              active={activeTab === 'answered'}
              onClick={() => setActiveTab('answered')}
            >
              Answered
            </TabButton>
          </div>

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search questions..."
              className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Questions list */}
        {sortedQuestions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No questions found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery
                ? 'Try adjusting your search terms'
                : activeTab === 'answered'
                ? 'No answered questions yet'
                : 'Be the first to ask a question!'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedQuestions.map((question) => (
              <QuestionCard
                key={question.id}
                question={question}
                currentUserId={user?.objectId || ''}
                onVote={() => handleVote(question.id)}
                onUnvote={() => handleUnvote(question.id)}
                formatTimeAgo={formatTimeAgo}
              />
            ))}
          </div>
        )}

        {/* Stats footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          Showing {sortedQuestions.length} of {questions.length} questions
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
        active
          ? 'bg-white text-blue-900 shadow-sm'
          : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  );
}

function QuestionCard({
  question,
  currentUserId,
  onVote,
  onUnvote,
  formatTimeAgo,
}: {
  question: Question;
  currentUserId: string;
  onVote: () => void;
  onUnvote: () => void;
  formatTimeAgo: (date: string) => string;
}) {
  const [isVoting, setIsVoting] = useState(false);
  const isOwner = currentUserId === question.createdByObjectId;
  const isAnswered = question.status === 'answered';

  const handleVoteClick = async () => {
    if (isOwner || isVoting) return;
    setIsVoting(true);
    try {
      if (question.hasUserVoted) {
        await onUnvote();
      } else {
        await onVote();
      }
    } finally {
      setIsVoting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex gap-4">
        {/* Vote button */}
        <div className="flex flex-col items-center">
          <button
            onClick={handleVoteClick}
            disabled={isOwner || isVoting}
            className={`w-12 h-12 flex items-center justify-center rounded-lg transition-all ${
              question.hasUserVoted
                ? 'bg-blue-100 text-blue-800'
                : isOwner
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-800'
            }`}
            title={
              isOwner
                ? "You can't vote on your own question"
                : question.hasUserVoted
                ? 'Click to remove your vote'
                : 'Click to upvote'
            }
          >
            <svg
              className="w-6 h-6"
              fill={question.hasUserVoted ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            </svg>
          </button>
          <span
            className={`mt-1 text-lg font-bold ${
              question.hasUserVoted ? 'text-blue-800' : 'text-gray-700'
            }`}
          >
            {question.voteCount}
          </span>
        </div>

        {/* Question content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <p className="text-gray-900 text-base leading-relaxed">
              {question.questionText}
            </p>
            {isAnswered && (
              <span className="flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                <svg
                  className="w-3 h-3 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Answered
              </span>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              {question.isAnonymous ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              )}
              {question.createdByName}
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {formatTimeAgo(question.createdDate)}
            </span>
            {question.hasUserVoted && (
              <span className="text-blue-800 font-medium">You voted</span>
            )}
            {isOwner && (
              <span className="text-gray-400">Your question</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
